import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import type { SphereAttachment } from 'types/attachment';
import { INLINE_PREVIEW_ALLOWLIST } from 'types/attachment';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import './AttachmentPreviewMolecule.scss';

const SIGNED_URL_MAX_BYTES = 25 * 1024 * 1024;

export type AttachmentPreviewKind =
  | 'image'
  | 'pdf'
  | 'video'
  | 'link'
  | 'tooLarge'
  | 'unsupported'
  | 'scanPending'
  | 'removed';

export interface AttachmentPreviewMoleculeProps {
  attachment: SphereAttachment;
  fetchSignedUrl?: (attachmentId: string) => Promise<string>;
  onDownload?: (attachment: SphereAttachment) => void;
  className?: string;
}

const PDF_MIME = 'application/pdf';
const VIDEO_MIMES: ReadonlySet<string> = new Set(['video/mp4', 'video/webm', 'video/ogg']);

function getPreviewKind(attachment: SphereAttachment): AttachmentPreviewKind {
  if (attachment.scan_status === 'pending') return 'scanPending';
  if (attachment.scan_status === 'malicious' || attachment.scan_status === 'quarantined') {
    return 'removed';
  }
  if (attachment.attachment_type === 'link') return 'link';
  const mime = attachment.mime_type ?? '';
  const size = attachment.size_bytes;
  if (!INLINE_PREVIEW_ALLOWLIST.has(mime)) return 'unsupported';
  if (typeof size === 'number' && size > SIGNED_URL_MAX_BYTES) return 'tooLarge';
  if (mime === PDF_MIME) return 'pdf';
  if (VIDEO_MIMES.has(mime)) return 'video';
  return 'image';
}

/**
 * Extract an HTTP status code from a signed-URL fetch error. Supports axios
 * (`err.response.status`), fetch-Response-style wrappers (`err.status`), and
 * plain numeric payloads without coupling the atom to a specific HTTP client.
 */
function getErrorStatus(err: unknown): number | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const e = err as { status?: unknown; response?: { status?: unknown } };
  if (typeof e.status === 'number') return e.status;
  if (e.response && typeof e.response.status === 'number') return e.response.status;
  return undefined;
}

const AttachmentPreviewMolecule: React.FC<AttachmentPreviewMoleculeProps> = ({
  attachment,
  fetchSignedUrl,
  onDownload,
  className = '',
}) => {
  const { t } = useTranslation();
  const kind = getPreviewKind(attachment);
  const needsSignedUrl = kind === 'image' || kind === 'pdf' || kind === 'video';

  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [fetchStatus, setFetchStatus] = useState<number | undefined>(undefined);
  const [reloadKey, setReloadKey] = useState(0);
  const retryCountRef = useRef(0);

  useEffect(() => {
    retryCountRef.current = 0;
    setFetchStatus(undefined);
  }, [attachment.id]);

  useEffect(() => {
    if (!needsSignedUrl || !fetchSignedUrl) return undefined;
    let cancelled = false;
    setIsLoading(true);
    setLoadFailed(false);
    setFetchStatus(undefined);
    fetchSignedUrl(attachment.id)
      .then((url) => {
        if (cancelled) return;
        setSignedUrl(url);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setSignedUrl(null);
        setFetchStatus(getErrorStatus(err));
        setLoadFailed(true);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [attachment.id, needsSignedUrl, fetchSignedUrl, reloadKey]);

  const handleMediaError = useCallback((): void => {
    if (retryCountRef.current >= 1) {
      setSignedUrl(null);
      setLoadFailed(true);
      return;
    }
    retryCountRef.current += 1;
    setReloadKey((k) => k + 1);
  }, []);

  const handleDownloadClick = useCallback((): void => {
    onDownload?.(attachment);
  }, [attachment, onDownload]);

  const handleOpenLink = useCallback((): void => {
    if (!attachment.url) return;
    window.open(attachment.url, '_blank', 'noopener,noreferrer');
  }, [attachment.url]);

  const displayName = attachment.name?.trim() || t('attachments.unnamed');

  const rootClasses = [
    'attachment-preview-molecule',
    `attachment-preview-molecule--${kind}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const renderDownloadFallback = (message: string) => (
    <output className="attachment-preview-molecule__fallback">
      <i className="pi pi-file" aria-hidden="true" />
      <p className="attachment-preview-molecule__message">{message}</p>
      {onDownload && (
        <ButtonAtom
          variant="primary"
          icon="pi pi-download"
          label={t('attachments.preview.downloadInstead')}
          onClick={handleDownloadClick}
          type="button"
        />
      )}
    </output>
  );

  const renderStatusFallback = (
    message: string,
    variant: 'scanPending' | 'removed',
  ) => (
    <output
      className={`attachment-preview-molecule__fallback attachment-preview-molecule__fallback--${variant}`}
    >
      <i
        className={variant === 'scanPending' ? 'pi pi-spin pi-spinner' : 'pi pi-shield'}
        aria-hidden="true"
      />
      <p className="attachment-preview-molecule__message">{message}</p>
    </output>
  );

  if (kind === 'link') {
    return (
      <div className={rootClasses} data-attachment-id={attachment.id}>
        <div className="attachment-preview-molecule__link">
          <i className="pi pi-link" aria-hidden="true" />
          <p className="attachment-preview-molecule__name" title={displayName}>
            {displayName}
          </p>
          {attachment.url && (
            <p
              className="attachment-preview-molecule__url"
              title={attachment.url}
            >
              {attachment.url}
            </p>
          )}
          <ButtonAtom
            variant="primary"
            icon="pi pi-external-link"
            label={t('attachments.openLink')}
            onClick={handleOpenLink}
            disabled={!attachment.url}
            type="button"
          />
        </div>
      </div>
    );
  }

  if (kind === 'scanPending') {
    return (
      <div className={rootClasses} data-attachment-id={attachment.id}>
        {renderStatusFallback(t('attachments.preview.scanPending'), 'scanPending')}
      </div>
    );
  }

  if (kind === 'removed') {
    return (
      <div className={rootClasses} data-attachment-id={attachment.id}>
        {renderStatusFallback(t('attachments.preview.removedMalicious'), 'removed')}
      </div>
    );
  }

  if (kind === 'tooLarge') {
    return (
      <div className={rootClasses} data-attachment-id={attachment.id}>
        {renderDownloadFallback(t('attachments.preview.tooLarge'))}
      </div>
    );
  }

  if (kind === 'unsupported') {
    return (
      <div className={rootClasses} data-attachment-id={attachment.id}>
        {renderDownloadFallback(t('attachments.preview.unsupported'))}
      </div>
    );
  }

  if (loadFailed) {
    // Back-door status handling: the local `attachment.scan_status` was
    // `'clean'` but the server responded 409/410, meaning the verdict changed
    // between list-fetch and signed-URL-fetch. Surface the specific reason
    // rather than the generic load-failed message.
    if (fetchStatus === 409) {
      return (
        <div className={rootClasses} data-attachment-id={attachment.id}>
          {renderStatusFallback(t('attachments.preview.scanPending'), 'scanPending')}
        </div>
      );
    }
    if (fetchStatus === 410) {
      return (
        <div className={rootClasses} data-attachment-id={attachment.id}>
          {renderStatusFallback(t('attachments.preview.removedMalicious'), 'removed')}
        </div>
      );
    }
    return (
      <div className={rootClasses} data-attachment-id={attachment.id}>
        {renderDownloadFallback(t('attachments.preview.loadFailed'))}
      </div>
    );
  }

  if (isLoading || !signedUrl) {
    return (
      <div className={rootClasses} data-attachment-id={attachment.id}>
        <output className="attachment-preview-molecule__loading">
          <i className="pi pi-spin pi-spinner" aria-hidden="true" />
          <span>{t('attachments.preview.loading')}</span>
        </output>
      </div>
    );
  }

  return (
    <div className={rootClasses} data-attachment-id={attachment.id}>
      {kind === 'image' && (
        <img
          className="attachment-preview-molecule__image"
          src={signedUrl}
          alt={displayName}
          onError={handleMediaError}
        />
      )}
      {kind === 'pdf' && (
        <embed
          className="attachment-preview-molecule__pdf"
          src={signedUrl}
          type="application/pdf"
          title={displayName}
        />
      )}
      {kind === 'video' && (
        <video
          className="attachment-preview-molecule__video"
          src={signedUrl}
          controls
          onError={handleMediaError}
        >
          <track kind="captions" />
        </video>
      )}
    </div>
  );
};

export default AttachmentPreviewMolecule;
